import React, { useState, useEffect, Suspense } from "react";
import { Canvas } from "@react-three/fiber";
import { OrbitControls, useGLTF } from "@react-three/drei";
import "../styles/GeoCar.css";
import * as THREE from "three";
import { useParams } from "react-router-dom";
import { Settings } from "lucide-react";



const CarModel = ({ onClick }) => {
  const { scene } = useGLTF("/cyberpunk_car/scene.gltf");
  const [carScene, setCarScene] = useState(null);
  const [scale, setScale] = useState(0.05);

  useEffect(() => {
    const handleResize = () => {
      const width = window.innerWidth;
      setScale(width < 768 ? 0.05 : 0.05);
    };
    handleResize();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    if (scene) {
      const clonedScene = scene.clone(true);
      clonedScene.traverse((child) => {
        if (child.isMesh) {
          child.material = child.material.clone();
          child.material.emissive = new THREE.Color(0xff00ff);
          child.material.emissiveIntensity = 5;
        }
      });
      setCarScene(clonedScene);
    }
  }, [scene]);

  if (!carScene) return null;

  return (
    <primitive
      object={carScene}
      scale={[scale, scale, scale]}
      position={[0, -5, 0]}
      rotation={[Math.PI / 0.1, -Math.PI / 0.21, 0]}
      onClick={onClick}
    />
  );
};

const GeoCar = () => {
  const { id } = useParams();
  const [location, setLocation] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [isChangingApp, setIsChangingApp] = useState(false);
  const [showPasswordPrompt, setShowPasswordPrompt] = useState(true);
  const [password, setPassword] = useState("");
  const [isPasswordCorrect, setIsPasswordCorrect] = useState(false);
  const [selectedApp, setSelectedApp] = useState("");
  const [status, setStatus] = useState("");
  const [errorMessage, setErrorMessage] = useState("");

  useEffect(() => {
    document.body.classList.add("geo-car-page");
    return () => {
      document.body.classList.remove("geo-car-page");
    };
  }, []);

  useEffect(() => {
    const savedLocation = localStorage.getItem("geoLocation");
    if (savedLocation) {
      setLocation(JSON.parse(savedLocation));
    }
  }, []);

  const getLocation = () => {
    setLoading(true);
    setError(null);

    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          const lat = position.coords.latitude;
          const lng = position.coords.longitude;
          const newLocation = { lat, lng };
          setLocation(newLocation);
          localStorage.setItem("geoLocation", JSON.stringify(newLocation));
          setLoading(false);
        },
        (error) => {
          console.error("Błąd pobierania lokalizacji:", error);
          setError("Włącz GPS i spróbuj ponownie.");
          setLoading(false);
        },
        {
          enableHighAccuracy: true,
          timeout: 10000,
          maximumAge: 0,
        }
      );
    } else {
      setError("Twoja przeglądarka nie obsługuje geolokalizacji.");
      setLoading(false);
    }
  };

  const showLocationOnMaps = () => {
    if (location) {
      const mapsUrl = `https://www.google.com/maps/search/?api=1&query=${location.lat},${location.lng}`;
      window.open(mapsUrl, "_blank");
    } else {
      alert("Najpierw kliknij 'Aktualizuj lokalizację'!");
    }
  };

  const checkPassword = async () => {
    setStatus("🔍 Sprawdzam hasło...");
    setErrorMessage("");

    try {
      const response = await fetch(`${process.env.REACT_APP_Verify_Password}/${id}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ password }),
      });

      const data = await response.json();
      if (data.success) {
        setIsPasswordCorrect(true);
        setShowPasswordPrompt(false);
      } else {
        setErrorMessage("❌ Niepoprawne hasło!");
      }
    } catch {
      setErrorMessage("❌ Błąd podczas sprawdzania hasła.");
    }
    setStatus("");
  };

  const changeApp = async () => {
    if (!selectedApp) {
      setErrorMessage("❌ Wybierz aplikację.");
      return;
    }

    setStatus("🔄 Zmieniam aplikację...");
    try {
      const response = await fetch(`${process.env.REACT_APP_Update_AppId}/${id}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ app_id: selectedApp }),
      });

      const data = await response.json();
      if (data.success) {
        window.location.href = `/${id}`;
      } else {
        setErrorMessage("❌ Nie udało się zmienić aplikacji.");
      }
    } catch {
      setErrorMessage("❌ Błąd serwera.");
    } finally {
      setStatus("");
    }
  };

  return (
    <>
    
      <button className="gear-button-floating" onClick={() => {
        setIsChangingApp(true);
        setPassword("");
        setIsPasswordCorrect(false);
        setErrorMessage("");
        setShowPasswordPrompt(true);
      }}>
        <Settings size={20} strokeWidth={2.5} />
      </button>

      {!isChangingApp && (
        <div className="geo-car-wrapper fade-in">
          <img src="/LogoV2.svg" alt="GeoCar logo" className="geo-car-logo" />
          <div className="geo-car-canvas">
            <Canvas camera={{ position: [30, 1, 4], fov: 45 }} style={{ width: "100dvw", height: "100%" }}>
              <hemisphereLight skyColor={0xffeeb1} groundColor="#101010" intensity={2} />
              <ambientLight intensity={0.6} />
              <pointLight position={[0, -2, 0]} intensity={1} color={0xff00ff} decay={1} distance={10} />
              <directionalLight position={[20, 30, 5]} intensity={8} />
              <Suspense fallback={null}>
                <CarModel onClick={getLocation} />
              </Suspense>
              <OrbitControls enableZoom={false} />
            </Canvas>
          </div>

          <div className="geo-car-content">
            <button onClick={getLocation} disabled={loading}>
              {loading ? "Pobieranie lokalizacji..." : "Aktualizuj lokalizację"}
            </button>
            <br /><br />
            <button onClick={showLocationOnMaps} disabled={!location}>
              Pokaż lokalizację
            </button>
            {location && <p><strong>📍 Twoja lokalizacja:</strong><br />Lat: {location.lat}, Lng: {location.lng}</p>}
            {error && <p className="error">{error}</p>}
          </div>
        </div>
      )}

{isChangingApp && showPasswordPrompt && (
  <div className="geocar-password-container fade-transition">

          <h3 className="geocar-password-title">🔑 Podaj hasło</h3>
          <input
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            className="geocar-password-input"
            placeholder="Wpisz hasło"
          />
          <button className="geocar-password-button" onClick={checkPassword}>
            {status || "Sprawdź"}
          </button>
          <button className="geocar-cancel-button" onClick={() => setIsChangingApp(false)}>
            Anuluj
          </button>
          {errorMessage && <p className="geocar-error">{errorMessage}</p>}
        </div>
      )}

{isChangingApp && isPasswordCorrect && (
  <div className="geocar-password-container fade-transition">

          <h3 className="geocar-password-title">⚙️ Wybierz aplikację</h3>
          <label className="geocar-radio-label">
            <input type="radio" value="viewer" checked={selectedApp === "viewer"} onChange={() => setSelectedApp("viewer")} />
            PinkFoto
          </label>
          <label className="geocar-radio-label">
            <input type="radio" value="geocar" checked={selectedApp === "geocar"} onChange={() => setSelectedApp("geocar")} />
            PinkCar
          </label>
          <button className="geocar-password-button" onClick={changeApp}>
            {status || "Zapisz i przejdź"}
          </button>
          <button className="geocar-cancel-button" onClick={() => {
            setIsChangingApp(false);
            setIsPasswordCorrect(false);
          }}>
            Cofnij
          </button>
          {errorMessage && <p className="geocar-error">{errorMessage}</p>}
        </div>
      )}
    </>
  );
};

useGLTF.preload("/cyberpunk_car/scene.gltf");
export default GeoCar;
